/** Consecutive activity from the same person groups when each event is within this long of the previous one. */
export const ACTIVITY_GROUP_WINDOW_MS = 60 * 1000;

export type ChangeType =
  | 'status'
  | 'severity'
  | 'assignee_add'
  | 'assignee_remove'
  | 'description'
  | 'title'
  | 'created'
  | 'other';

export type StreamPhrase = {
  type: ChangeType;
  lead: string;
  value?: string;
  suggestedByAi?: boolean;
};

export type DescriptionDiff = {
  previous: string;
  current: string;
};

export type ActivityStreamEntry = {
  kind: 'activity';
  id: string;
  timestamp: number;
  actorName: string;
  phrases: StreamPhrase[];
  descriptionDiff: DescriptionDiff | null;
};

export type CommentStreamEntry<T> = {
  kind: 'comment';
  id: string;
  timestamp: number;
  comment: T;
};

export type StreamEntry<T> = CommentStreamEntry<T> | ActivityStreamEntry;

export type StreamLog = {
  id: number;
  createdAt: string;
  userId?: number;
  user?: { id?: number; name?: string } | null;
  changesHtml?: string | string[] | null;
  titles?: ReadonlyArray<string | null | undefined> | null;
  descriptions?: ReadonlyArray<string | null | undefined> | null;
};

type ParsedChange = {
  type: ChangeType;
  value?: string;
  names?: string[];
  phrase?: string;
};

type ActivityDraft = {
  ids: number[];
  timestamp: number;
  lastTimestamp: number;
  actorId: number;
  actorName: string;
  changes: ParsedChange[];
  titleRemoved: boolean;
  suggestedByAi: boolean;
  descriptionDiff: DescriptionDiff | null;
};

const KNOWN_PREFIXES: { prefix: string; type: ChangeType }[] = [
  { prefix: 'created task from merge of ', type: 'other' },
  { prefix: 'added assignees ', type: 'assignee_add' },
  { prefix: 'removed assignees ', type: 'assignee_remove' },
  { prefix: 'added assignee ', type: 'assignee_add' },
  { prefix: 'removed assignee ', type: 'assignee_remove' },
  { prefix: 'attached file ', type: 'other' },
  { prefix: 'deleted an attachment', type: 'other' },
  { prefix: 'added tags ', type: 'other' },
  { prefix: 'removed tags ', type: 'other' },
  { prefix: 'added tag ', type: 'other' },
  { prefix: 'removed tag ', type: 'other' },
  { prefix: 'set due date to ', type: 'other' },
  { prefix: 'set status to ', type: 'status' },
  { prefix: 'set severity to ', type: 'severity' },
  { prefix: 'changed description', type: 'description' },
  { prefix: 'changed title', type: 'title' },
  { prefix: 'merged into task ', type: 'other' },
  { prefix: 'merged tasks ', type: 'other' },
  { prefix: 'merged task ', type: 'other' },
  { prefix: 'sent to ', type: 'other' },
  { prefix: 'created task', type: 'created' }
];

const timestampOf = (createdAt: string): number => {
  const time = Date.parse(createdAt);
  return Number.isNaN(time) ? 0 : time;
};

const actorIdOf = (log: StreamLog): number => {
  if (typeof log.user?.id === 'number') return log.user.id;
  if (typeof log.userId === 'number') return log.userId;
  return -1;
};

const actorNameOf = (log: StreamLog): string => log.user?.name || 'Anonymous';

const otherPhrase = (prefix: string, value: string): string => {
  const head = prefix.trim();
  return value ? `${head} ${value}` : head;
};

const stripSeparator = (value: string): string =>
  value
    .replace(/^\s+/, '')
    .replace(/,\s*$/, '')
    .trim();

export const activityChangesText = (changesHtml: unknown): string => {
  if (Array.isArray(changesHtml)) return changesHtml.filter(Boolean).join(', ');
  return typeof changesHtml === 'string' ? changesHtml : '';
};

export const parseActivityChanges = (changesHtml: unknown): ParsedChange[] => {
  const text = activityChangesText(changesHtml).trim();
  if (!text) return [];

  const matches: { index: number; prefix: string; type: ChangeType }[] = [];
  let index = 0;
  while (index < text.length) {
    const match = KNOWN_PREFIXES.find(item =>
      text.startsWith(item.prefix, index)
    );
    if (match) {
      matches.push({ index, prefix: match.prefix, type: match.type });
      index += match.prefix.length;
    } else {
      index += 1;
    }
  }

  if (matches.length === 0) return [{ type: 'other', phrase: text }];

  const changes: ParsedChange[] = [];
  const lead = stripSeparator(text.slice(0, matches[0].index));
  if (lead) changes.push({ type: 'other', phrase: lead });

  matches.forEach((match, matchIndex) => {
    const valueEnd = matches[matchIndex + 1]?.index ?? text.length;
    const value = stripSeparator(
      text.slice(match.index + match.prefix.length, valueEnd)
    );

    if (match.type === 'assignee_add' || match.type === 'assignee_remove') {
      const names = value
        .split(/,\s*/)
        .map(name => name.trim())
        .filter(Boolean);
      if (names.length) changes.push({ type: match.type, names });
      return;
    }

    if (match.type === 'status' || match.type === 'severity') {
      changes.push({ type: match.type, value });
      return;
    }

    if (match.type === 'other' || match.type === 'created') {
      const phrase = otherPhrase(match.prefix, value);
      if (phrase) changes.push({ type: match.type, phrase });
      return;
    }

    changes.push({ type: match.type });
  });

  return changes;
};

const descriptionDiffOf = (log: StreamLog): DescriptionDiff | null => {
  const previous = log.descriptions?.[0];
  const current = log.descriptions?.[1];
  if (!previous || !current) return null;
  return { previous, current };
};

const titleStateOf = (
  log: StreamLog
): { removed: boolean; suggestedByAi: boolean } => {
  const nextTitle = log.titles?.[1];
  return {
    removed:
      Array.isArray(log.titles) && (nextTitle == null || nextTitle === ''),
    suggestedByAi:
      typeof nextTitle === 'string' && nextTitle.includes(' (Suggested by AI)')
  };
};

const applyChange = (
  draft: ActivityDraft,
  change: ParsedChange,
  log: StreamLog
) => {
  if (change.type === 'status' || change.type === 'severity') {
    const existing = draft.changes.find(item => item.type === change.type);
    if (existing) {
      existing.value = change.value;
      return;
    }
    draft.changes.push({ type: change.type, value: change.value });
    return;
  }

  if (change.type === 'assignee_add' || change.type === 'assignee_remove') {
    const existing = draft.changes.find(item => item.type === change.type);
    const names = change.names || [];
    if (!existing) {
      draft.changes.push({ type: change.type, names: [...names] });
      return;
    }
    const seen = new Set(existing.names || []);
    names.forEach(name => {
      if (seen.has(name)) return;
      seen.add(name);
      existing.names = [...(existing.names || []), name];
    });
    return;
  }

  if (change.type === 'description') {
    if (!draft.changes.some(item => item.type === 'description')) {
      draft.changes.push({ type: 'description' });
    }
    draft.descriptionDiff = descriptionDiffOf(log);
    return;
  }

  if (change.type === 'title') {
    if (!draft.changes.some(item => item.type === 'title')) {
      draft.changes.push({ type: 'title' });
    }
    const titleState = titleStateOf(log);
    draft.titleRemoved = titleState.removed;
    draft.suggestedByAi = titleState.suggestedByAi;
    return;
  }

  const phrase = change.phrase || '';
  if (!phrase) return;
  if (
    draft.changes.some(
      item => item.type === change.type && item.phrase === phrase
    )
  ) {
    return;
  }
  draft.changes.push({ type: change.type, phrase });
};

const assigneeLead = (verb: 'added' | 'removed', names: string[]): string => {
  const noun = names.length === 1 ? 'assignee' : 'assignees';
  return `${verb} ${noun} `;
};

const phrasesFrom = (draft: ActivityDraft): StreamPhrase[] =>
  draft.changes
    .map((change): StreamPhrase | null => {
      if (change.type === 'status') {
        return {
          type: 'status',
          lead: 'set status to ',
          value: change.value || ''
        };
      }
      if (change.type === 'severity') {
        return {
          type: 'severity',
          lead: 'set severity to ',
          value: change.value || ''
        };
      }
      if (change.type === 'assignee_add' || change.type === 'assignee_remove') {
        const names = change.names || [];
        if (!names.length) return null;
        const verb = change.type === 'assignee_add' ? 'added' : 'removed';
        return {
          type: change.type,
          lead: assigneeLead(verb, names),
          value: names.join(', ')
        };
      }
      if (change.type === 'description') {
        return { type: 'description', lead: 'changed description' };
      }
      if (change.type === 'title') {
        return {
          type: 'title',
          lead: draft.titleRemoved ? 'removed title' : 'changed title',
          suggestedByAi: draft.suggestedByAi
        };
      }
      if (!change.phrase) return null;
      return { type: change.type, lead: change.phrase };
    })
    .filter((phrase): phrase is StreamPhrase => phrase !== null);

const entryFromDraft = (draft: ActivityDraft): ActivityStreamEntry | null => {
  const phrases = phrasesFrom(draft);
  if (!phrases.length) return null;
  return {
    kind: 'activity',
    id: `activity-${draft.ids.join('-')}`,
    timestamp: draft.timestamp,
    actorName: draft.actorName,
    phrases,
    descriptionDiff: draft.changes.some(change => change.type === 'description')
      ? draft.descriptionDiff
      : null
  };
};

const startDraft = (log: StreamLog, timestamp: number): ActivityDraft => ({
  ids: [],
  timestamp,
  lastTimestamp: timestamp,
  actorId: actorIdOf(log),
  actorName: actorNameOf(log),
  changes: [],
  titleRemoved: false,
  suggestedByAi: false,
  descriptionDiff: null
});

const applyLog = (draft: ActivityDraft, log: StreamLog, timestamp: number) => {
  parseActivityChanges(log.changesHtml).forEach(change =>
    applyChange(draft, change, log)
  );
  draft.ids.push(log.id);
  draft.timestamp = timestamp;
  draft.lastTimestamp = timestamp;
  if (log.user?.name) draft.actorName = log.user.name;
};

export const buildCommentStream = <T extends { id: number; createdAt: string }>(
  comments: T[],
  logs: StreamLog[],
  { groupActivity }: { groupActivity: boolean }
): StreamEntry<T>[] => {
  type Timed =
    | { kind: 'comment'; timestamp: number; comment: T }
    | { kind: 'activity'; timestamp: number; log: StreamLog };

  const events: Timed[] = [
    ...comments.map(comment => ({
      kind: 'comment' as const,
      timestamp: timestampOf(comment.createdAt),
      comment
    })),
    ...logs
      .filter(log => log && activityChangesText(log.changesHtml).trim())
      .map(log => ({
        kind: 'activity' as const,
        timestamp: timestampOf(log.createdAt),
        log
      }))
  ].sort(
    (a, b) => a.timestamp - b.timestamp || (a.kind === 'activity' ? -1 : 1)
  );

  const entries: StreamEntry<T>[] = [];
  let draft: ActivityDraft | null = null;

  const closeDraft = () => {
    if (!draft) return;
    const entry = entryFromDraft(draft);
    if (entry) entries.push(entry);
    draft = null;
  };

  events.forEach(event => {
    if (event.kind === 'comment') {
      closeDraft();
      entries.push({
        kind: 'comment',
        id: `comment-${event.comment.id}`,
        timestamp: event.timestamp,
        comment: event.comment
      });
      return;
    }

    if (!parseActivityChanges(event.log.changesHtml).length) return;

    const sameActor = draft !== null && draft.actorId === actorIdOf(event.log);
    const withinWindow =
      draft !== null &&
      event.timestamp - draft.lastTimestamp <= ACTIVITY_GROUP_WINDOW_MS;

    if (groupActivity && draft && sameActor && withinWindow) {
      applyLog(draft, event.log, event.timestamp);
      return;
    }

    closeDraft();
    draft = startDraft(event.log, event.timestamp);
    applyLog(draft, event.log, event.timestamp);
  });

  closeDraft();

  return entries.sort((a, b) => b.timestamp - a.timestamp);
};
